import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Save, Truck, CheckCircle2, AlertCircle, Loader2, ExternalLink, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';

/**
 * `HH:MM:SS` → `12:00 PM`. The column used to print the stored string raw, so a pickup slot read as
 * `2026-08-06 · 12:00:00` — a machine format on a screen ops scan all day. Falls back to whatever is
 * stored if it isn't a time, so legacy rows still show something rather than blanking out.
 */
function formatPickupTime(t?: string | null): string {
  const m = String(t ?? '').match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t || '—';
  const h = Number(m[1]);
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${suffix}`;
}

/** `12:00` (what an <input type="time"> gives) → `12:00:00`, the format couriers want. */
function toHms(t: string): string {
  const p = String(t ?? '').split(':');
  if (p.length < 2) return t;
  return `${p[0].padStart(2, '0')}:${p[1].padStart(2, '0')}:${(p[2] ?? '00').padStart(2, '0')}`;
}

/** `2026-08-06` → `06 Aug 2026`, with today/tomorrow called out — the two that matter operationally. */
function formatPickupDate(d?: string | null): { label: string; relative: string | null } {
  const s = String(d ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return { label: s || '—', relative: null };
  const [y, mo, da] = s.split('-').map(Number);
  const date = new Date(y, mo - 1, da);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((date.getTime() - today.getTime()) / 86400000);
  return {
    label: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
    relative: diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : diff === -1 ? 'Yesterday' : null,
  };
}

interface Courier { id: number; name: string; code: string; orderType?: string }
interface Warehouse { id: number; name: string; city?: string; merchantId?: number }
interface Merchant { id: number; companyName: string }
interface PickupRow {
  id: number;
  merchantId?: number | null;
  warehouseId: number;
  courierId: number;
  courierCode: string;
  pickupLocationName: string;
  pickupDate: string;
  pickupTime: string;
  /** API emits both; `expectedPackages` is the column name, `expectedPackageCount` the DTO name. */
  expectedPackageCount?: number;
  expectedPackages?: number;
  status: string;
  courierRequestId?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  courier?: Courier | null;
  warehouse?: Warehouse | null;
  merchant?: Merchant | null;
}

export function PickupRequestsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'B2C'|'B2B'>('B2C');
  const isStaff = user?.kind === 'user' && ['SUPER_ADMIN','ADMIN','OPERATION'].includes(user.userType ?? '');
  if (!isStaff) return <Navigate to="/dashboard" replace />;

  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['pickup-requests'],
    queryFn: async () => (await api.get<PickupRow[]>('/pickup-requests')).data,
  });

  // Which row is mid-retry, so only that row's button spins rather than all of them.
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const retry = useMutation({
    mutationFn: async (id: number) => { setRetryingId(id); return (await api.post(`/pickup-requests/${id}/retry`)).data; },
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['pickup-requests'] });
      if (r?.ok) toast.success(r.msg); else toast.error(r?.msg ?? 'Retry failed');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Retry failed'),
    onSettled: () => setRetryingId(null),
  });

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Truck size={22} className="text-emerald-700" /> Pickup Requests
        </h1>
        <button onClick={() => setShowCreate(true)} className="px-3 py-2 bg-emerald-600 text-white rounded text-sm flex items-center gap-1">
          <Plus size={14} /> New Pickup Request
        </button>
      </div>

            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 mb-3 w-fit">
        {(['B2C','B2B'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-1.5 text-sm rounded-md transition ${tab === t ? 'bg-white shadow-sm text-emerald-700 font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
            Pickup {t}
          </button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-emerald-50">
            <tr>
              <th className="px-3 py-2 text-left">ID</th>
              <th className="px-3 py-2 text-left">Date / Time</th>
              <th className="px-3 py-2 text-left">Courier</th>
              <th className="px-3 py-2 text-left">Warehouse</th>
              <th className="px-3 py-2 text-left">Merchant</th>
              <th className="px-3 py-2 text-right">Pkgs</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Pickup ID</th>
              <th className="px-3 py-2 text-left">Notes</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={9} className="text-center py-6 text-slate-400">Loading…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-slate-400">No pickup requests yet — click "New Pickup Request"</td></tr>}
            {rows.filter((r) => (r.courier?.orderType ?? 'B2C') === tab).map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-3 py-1.5 text-xs">#{r.id}</td>
                <td className="px-3 py-1.5 text-xs whitespace-nowrap">
                  {(() => {
                    const d = formatPickupDate(r.pickupDate);
                    return (
                      <>
                        <div className="font-medium text-slate-800 flex items-center gap-1.5">
                          {d.label}
                          {d.relative && (
                            <span className={`text-[10px] px-1.5 rounded ${d.relative === 'Today' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                              {d.relative}
                            </span>
                          )}
                        </div>
                        {/* Readable slot, with the EXACT value the user entered beside it — ops
                            quote that raw HH:MM:SS to the courier, so it has to stay visible. */}
                        <div className="text-slate-500 flex items-center gap-1 mt-0.5">
                          <Clock size={10} /> {formatPickupTime(r.pickupTime)}
                          <span className="text-slate-400 font-mono text-[10px]">({r.pickupTime ?? '—'})</span>
                        </div>
                      </>
                    );
                  })()}
                </td>
                <td className="px-3 py-1.5 text-xs">{r.courier?.name ?? r.courierCode}</td>
                <td className="px-3 py-1.5 text-xs">{r.warehouse?.name ?? `#${r.warehouseId}`}</td>
                <td className="px-3 py-1.5 text-xs">{r.merchant?.companyName ?? '—'}</td>
                {/* Read BOTH names. The column is `expectedPackages`; this cell asked only for
                    `expectedPackageCount` and so rendered blank on every row, which read as "the
                    user never entered a package count" when the number was in the database all
                    along. `?? 0` rather than a blank so a genuine zero is still visible. */}
                <td className="px-3 py-1.5 text-xs text-right font-medium text-slate-800">
                  {r.expectedPackageCount ?? r.expectedPackages ?? 0}
                </td>
                <td className="px-3 py-1.5 text-xs">
                  {r.status === 'CONFIRMED' && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} />Confirmed</span>}
                  {r.status === 'FAILED' && <span className="inline-flex items-center gap-1 text-rose-700"><AlertCircle size={12} />Failed</span>}
                  {r.status === 'REQUESTED' && <span className="text-amber-700">Requested</span>}
                  {r.status === 'CANCELLED' && <span className="text-slate-500">Cancelled</span>}
                </td>
                <td className="px-3 py-1.5 text-xs font-mono">{r.courierRequestId ?? '—'}</td>
                <td className="px-3 py-1.5 text-xs text-rose-700 max-w-xs">
                  <div className="truncate" title={r.errorMessage ?? ''}>{r.errorMessage ?? '—'}</div>
                  {r.status !== 'CONFIRMED' && (
                    <div className="flex items-center gap-2 mt-0.5">
                      {/* Re-fires the SAME request. Most of these failures are transient or were
                          fixed elsewhere (a warehouse registered since, a corrected endpoint), so
                          the useful action is "try again", not "retype all six fields". */}
                      <button onClick={() => retry.mutate(r.id)} disabled={retry.isPending && retryingId === r.id}
                        className="text-emerald-700 hover:underline inline-flex items-center gap-0.5 font-medium disabled:opacity-50">
                        {retry.isPending && retryingId === r.id
                          ? <><Loader2 size={10} className="animate-spin" /> Retrying…</>
                          : <><RefreshCw size={10} /> Retry</>}
                      </button>
                      <a href={`/courier-api-logs?entityId=${r.id}`} className="text-slate-500 hover:underline inline-flex items-center gap-0.5"><ExternalLink size={10} />log</a>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}

/**
 * The slot the form opens on — mirrors `nextPickupSlot()` on the server so the default the operator
 * is shown is one the server will accept. A convenience only: the server decides, and refuses a past
 * slot whatever this returns.
 */
function defaultSlot(): { date: string; time: string } {
  const p = (n: number) => String(n).padStart(2, '0');
  const ymd = (d: Date) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  const now = new Date();
  const nextHour = now.getHours() + 1;
  if (nextHour >= 18) {
    return { date: ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)), time: '10:00' };
  }
  return { date: ymd(now), time: `${p(nextHour)}:00` };
}

function CreateDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [merchantId, setMerchantId] = useState<number | ''>('');
  const [warehouseId, setWarehouseId] = useState<number | ''>('');
  const [courierId, setCourierId] = useState<number | ''>('');
  // Opens on the next slot the courier will actually accept, not a fixed 15:00 that is already in
  // the past for anyone creating a request in the afternoon (the server refuses those outright).
  // Same rule the auto-raised requests use: next full hour, rolling to tomorrow 10:00 after 6 PM.
  const [pickupDate, setPickupDate] = useState<string>(defaultSlot().date);
  const [pickupTime, setPickupTime] = useState<string>(defaultSlot().time);
  const [expectedPackageCount, setExpectedPackageCount] = useState<number>(1);

  const { data: merchants } = useQuery({ queryKey: ['merchants','all'], queryFn: async () => (await api.get<Merchant[]>('/merchants')).data });
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses-for-merchant', merchantId],
    queryFn: async () => (await api.get<Warehouse[]>('/warehouses', { params: merchantId ? { merchantId } : {} })).data,
  });
  const { data: couriers } = useQuery({ queryKey: ['couriers','active'], queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { isActive: 'true' } })).data });

  const submit = useMutation({
    mutationFn: async () => (await api.post('/pickup-requests', {
      merchantId: merchantId || undefined,
      warehouseId, courierId,
      pickupDate, pickupTime: toHms(pickupTime), expectedPackageCount,
    })).data,
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ['pickup-requests'] });
      if (r.ok) toast.success(r.msg);
      else toast.error(r.msg);
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create pickup request'),
  });

  const valid = warehouseId && courierId && pickupDate && pickupTime && expectedPackageCount > 0;

  return (
    <div className="fixed inset-0 bg-black/50 z-[10001] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">New Pickup Request</h2>
          <button onClick={onClose}><X size={18} className="text-slate-400 hover:text-slate-700" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Merchant (optional — filters warehouses)</label>
            <SearchableSelect value={merchantId === '' ? '' : merchantId}
              onChange={(v) => { setMerchantId(v === '' ? '' : Number(v)); setWarehouseId(''); }}
              options={(merchants ?? []).map((m) => ({ value: m.id, label: m.companyName + ' (#' + m.id + ')' }))}
              placeholder="All merchants" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Warehouse / Pickup Location <span className="text-rose-600">*</span></label>
            <SearchableSelect value={warehouseId === '' ? '' : warehouseId}
              onChange={(v) => setWarehouseId(v === '' ? '' : Number(v))}
              options={(warehouses ?? []).map((w) => ({ value: w.id, label: w.name + ' (#' + w.id + ')', hint: w.city ?? '' }))}
              placeholder="Select pickup warehouse" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Courier <span className="text-rose-600">*</span></label>
            <SearchableSelect value={courierId === '' ? '' : courierId}
              onChange={(v) => setCourierId(v === '' ? '' : Number(v))}
              options={(couriers ?? []).map((c) => ({ value: c.id, label: c.name + ' (#' + c.id + ')', hint: c.code }))}
              placeholder="Select courier" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Pickup Date <span className="text-rose-600">*</span></label>
              <input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Pickup Time <span className="text-rose-600">*</span></label>
              {/* A real time picker, not a free-text box. Typing "12 pm" or "12:00" into the old one
                  sent exactly that to the courier, and a slot they can't parse is a pickup that never
                  arrives. `HH:MM` here, `:00` appended on submit for the courier's HH:MM:SS. */}
              <input type="time" value={pickupTime.slice(0, 5)} onChange={(e) => setPickupTime(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm" />
              <div className="text-[10px] text-slate-400 mt-0.5">{formatPickupTime(pickupTime)} · sent as {toHms(pickupTime)}</div>
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Expected Packages <span className="text-rose-600">*</span></label>
              <input type="number" min={1} value={expectedPackageCount} onChange={(e) => setExpectedPackageCount(Math.max(1, Number(e.target.value)))}
                className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 border rounded p-2">
            On submit, the system calls the courier API immediately. If the courier returns a <code className="bg-white px-1 rounded">pickup_id</code> it's saved with status <b>Confirmed</b>. Otherwise the error message is stored with status <b>Failed</b> — open Courier API Logs to see the full request/response.
          </div>
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 border rounded text-sm">Cancel</button>
          <button onClick={() => submit.mutate()} disabled={!valid || submit.isPending}
            className="px-4 py-2 bg-emerald-600 text-white rounded text-sm flex items-center gap-1 disabled:opacity-50">
            {submit.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create Pickup Request
          </button>
        </div>
      </div>
    </div>
  );
}
